import React, { useEffect, useState, useCallback } from 'react';
import { leadAPI, dashboardAPI } from '../api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

const PIPELINE_STAGES = [
  { key: 'new_lead', label: 'New Lead', color: '#3B82F6' },
  { key: 'contacted', label: 'Contacted', color: '#8B5CF6' },
  { key: 'qualified', label: 'Qualified', color: '#06B6D4' },
  { key: 'site_visit_scheduled', label: 'Site Visit Scheduled', color: '#EC4899' },
  { key: 'site_visit_completed', label: 'Site Visit Completed', color: '#14B8A6' },
  { key: 'quotation_sent', label: 'Quotation Sent', color: '#F59E0B' },
  { key: 'negotiation', label: 'Negotiation', color: '#F97316' },
  { key: 'won', label: 'Won', color: '#22C55E' },
  { key: 'lost', label: 'Lost', color: '#EF4444' }
];

const LeadCard = ({ lead, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.id
  });
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1
  } : undefined;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      data-testid={`kanban-card-${lead.id}`}
      onClick={onClick}
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-sm cursor-move hover:shadow-md transition-shadow mb-2">
      <div className="flex items-start justify-between mb-2">
        <p className="font-medium text-sm truncate flex-1">{lead.customer_name}</p>
        <span className={`text-xs px-1.5 py-0.5 rounded ${
          lead.priority === 'urgent' ? 'bg-red-100 text-red-700' :
          lead.priority === 'high' ? 'bg-orange-100 text-orange-700' :
          'bg-slate-100 text-slate-700'
        }`}>
          {lead.priority?.toUpperCase()}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-1">{lead.primary_mobile}</p>
      {lead.services_required?.length > 0 && (
        <p className="text-xs text-muted-foreground mb-1 truncate">{lead.services_required.join(', ')}</p>
      )}
      {lead.budget && (
        <p className="text-xs font-medium text-primary">₹{(lead.budget / 100000).toFixed(1)}L</p>
      )}
    </div>
  );
};

const Column = ({ stage, leads, onCardClick }) => {
  const { isOver, setNodeRef } = useDroppable({ id: stage.key });
  return (
    <div ref={setNodeRef} data-testid={`kanban-col-${stage.key}`}
      className={`flex-shrink-0 w-72 bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 ${isOver ? 'ring-2 ring-primary' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }}></div>
          <h3 className="font-medium text-sm">{stage.label}</h3>
        </div>
        <span className="text-xs bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full">{leads.length}</span>
      </div>
      <div className="space-y-2 min-h-[100px] max-h-[calc(100vh-260px)] overflow-y-auto">
        {leads.map(lead => (
          <LeadCard key={lead.id} lead={lead} onClick={() => onCardClick(lead.id)} />
        ))}
      </div>
    </div>
  );
};

export const LeadPipelinePage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const fetchLeads = useCallback(async () => {
    try {
      const { data } = await leadAPI.getAll({ limit: 500 });
      setLeads(data);
    } catch (error) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const newStatus = over.id;
    const leadId = active.id;
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.status === newStatus) return;

    // Optimistic update
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));

    try {
      await leadAPI.update(leadId, { status: newStatus });
      toast.success(`Moved to ${PIPELINE_STAGES.find(s => s.key === newStatus)?.label || newStatus}`);
    } catch (error) {
      toast.error('Failed to update. Reverting.');
      fetchLeads();
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  const leadsByStage = {};
  PIPELINE_STAGES.forEach(s => { leadsByStage[s.key] = []; });
  // Also map legacy "converted" to "won"
  leads.forEach(lead => {
    const stage = lead.status === 'converted' ? 'won' : lead.status;
    if (leadsByStage[stage]) leadsByStage[stage].push(lead);
    else {
      // Unknown status - put in new_lead
      leadsByStage['new_lead'].push(lead);
    }
  });

  const activeLead = activeId ? leads.find(l => l.id === activeId) : null;

  return (
    <div className="space-y-6" data-testid="pipeline-container">
      <div>
        <h1 className="text-4xl font-outfit font-semibold">Lead Pipeline</h1>
        <p className="text-muted-foreground mt-2">Drag and drop leads to update their stage</p>
      </div>

      <DndContext sensors={sensors} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {PIPELINE_STAGES.map(stage => (
            <Column key={stage.key} stage={stage} leads={leadsByStage[stage.key] || []}
              onCardClick={(id) => navigate(`/leads/${id}`)} />
          ))}
        </div>
        <DragOverlay>
          {activeLead && <LeadCard lead={activeLead} onClick={() => {}} />}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
